/**
 * useNavBadges — single source of truth for the red-dot counters on
 * the mobile bottom-nav and (optionally) the desktop sidebar.
 *
 * Polls `/api/me/nav-badges` every 60 s by default. The hook returns
 * the most recent counts plus an `isStale` flag (true if the last
 * fetch failed or is older than 5 minutes).
 *
 * Why a hook instead of a context? Each consumer site (bottom-nav,
 * sidebar, dashboard kpi strip) is independent and the data is cheap
 * to fetch; a shared hook keeps coupling low.
 */
import { useEffect, useState, useRef } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const POLL_MS = 60_000;
const STALE_AFTER_MS = 5 * 60_000;

export default function useNavBadges() {
  const { user } = useAuth();
  const [data, setData] = useState({
    tasks_open: 0,
    tasks_overdue: 0,
    opportunities_stuck: 0,
    approvals_pending: 0,
    chat_unread: 0,
    pending_access: 0,
    as_of: null,
  });
  const [isStale, setIsStale] = useState(false);
  const lastSuccessRef = useRef(0);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const fetchOnce = async () => {
      try {
        const { data: payload } = await api.get('/me/nav-badges');
        if (cancelled) return;
        setData(payload);
        setIsStale(false);
        lastSuccessRef.current = Date.now();
      } catch (err) {
        // Network blips or 401 during token refresh — don't toast.
        if (Date.now() - lastSuccessRef.current > STALE_AFTER_MS) {
          setIsStale(true);
        }
      }
    };

    fetchOnce();
    const id = setInterval(fetchOnce, POLL_MS);
    // Re-fetch whenever the tab becomes visible again (mobile users
    // background the app constantly).
    const onVis = () => { if (document.visibilityState === 'visible') fetchOnce(); };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [user]);

  return { ...data, isStale };
}
